import Analytics from 'helpers/analytics';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PreviewEmailTypeEnum } from 'Views/Campaigns/enum/previewEmailType.enum';
import {
	getSectionCommunication,
	postSectionFile,
	postSectionPreSignedUrl,
	submitSectionForm,
} from 'Views/Campaigns/services/CampaignServices';
import {
	campaignTypes,
	initialCampaign,
	stepperOptions,
	superscriptionItems,
} from './NewCampaign.enums';
import { useToast } from '@welbe/web-design-system';

export const useNewCampaign = () => {
	const { toast } = useToast();
	const navigate = useNavigate();
	const [datesScheduled, setDatesScheduled] = useState<any[]>([]);
	const [isLoading, setLoading] = useState<boolean>(false);
	const [isFormLoading, setFormLoading] = useState<boolean>(false);
	const [hasError, setError] = useState<boolean>(false);
	const [isFinishModalOpen, setFinishModalOpen] = useState<boolean>(false);
	const [isLoadingUpload, setLoadingUpload] = useState<boolean>(false);
	const [createdForms, setCreatedForms] = useState<string[]>([]);
	const [campaign, setCampaign] = useState<any>(
		JSON.parse(JSON.stringify(initialCampaign)),
	);
	const [active, setActive] = useState<number>(0);
	const [completed, setCompleted] = useState<number>(0);
	const type = stepperOptions[active].type;
	const formTitle = window.atob(window.location.search.split('?name=')[1]);
	const campaignId = window.location.pathname.split('/')[3];

	const [
		showModalPreviewEmailCommunication,
		setShowModalPreviewEmailCommunication,
	] = useState(false);

	const handleFile = async (value: any) => {
		setLoadingUpload(true);

		const removeSpecialCaracter = (regexValue: string) =>
			regexValue.replace(/[^0-9a-zA-Z.]/g, '_').toLowerCase();

		const payload = {
			filename: removeSpecialCaracter(value.name),
		};

		try {
			const { data } = await postSectionPreSignedUrl(payload);
			await postSectionFile(data.content.url, value, value.type);
			value = data.content.filename;

			campaign[type].messageFile = value;
			campaign[type].fileName = value;
			setCampaign({ ...campaign });
		} catch (error) {
			toast.error('Hubo un error al subir el archivo');
		} finally {
			setLoadingUpload(false);
		}
	};

	const handleChange = async (value: any, field: string) => {
		if (field === 'sendingDate') {
			const date = value ? value : undefined;
			value = date;
		}

		if (field === 'messageFile') {
			handleFile(value);

			return;
		}

		if (field === 'useCustomTemplate') {
			campaign[type].messageBody = '';
			if (value === PreviewEmailTypeEnum.CUSTOM) {
				value = true;
			}
			if (value === PreviewEmailTypeEnum.DEFAULT) {
				value = false;
			}
		}

		campaign[type][field] = value;
		setCampaign({ ...campaign });
	};

	const handlePreviewEmailCommunication = () => {
		Analytics.track('campaign', 'show-create-campaign-preview');
		setShowModalPreviewEmailCommunication(true);
	};

	const handleClosePreviewEmailCommunication = () => {
		setShowModalPreviewEmailCommunication(false);
	};

	const getScheduledColor = async () => {
		const itemSection = superscriptionItems.find(
			(item: any) => item.type === type,
		);

		if (itemSection) {
			const newDateScheduled = datesScheduled.filter(
				(scheduled) => scheduled.color !== itemSection.color,
			);
			const color = itemSection.color;
			setDatesScheduled([
				...newDateScheduled,
				{
					color,
					date: campaign[type].sendingDate,
				},
			]);
		}
	};

	const validateForm = (content: any) => {
		if (
			!content.messageTitle ||
			!content.name ||
			!content.sendingTime ||
			!content.sendingDate
		) {
			return false;
		}

		return true;
	};

	const getMethod = (type: string) =>
		createdForms.includes(campaign[type].communicationTypeId) ? 'put' : 'post';

	const handleClick = async (step: string) => {
		let index = active;
		let isLast = false;

		if (step === 'next') {
			try {
				setLoading(true);
				const isValid = validateForm(campaign[type]);
				if (isValid) {
					setError(false);
					const method = getMethod(type);
					campaign[type].campaignId = campaignId;
					if (method === 'put') {
						campaign[type].messageFile = campaign[type].fileName
							? campaign[type].fileName.split('campaign/communications/').pop()
							: '';
					}

					Analytics.track(
						'campaign',
						`create-campaign-next-step-${type}`,
						campaign[type],
					);
					await submitSectionForm(campaign[type], method);
					await getScheduledColor();
					if (campaign[type].communicationTypeId === 'nom035-closing') {
						isLast = true;
						setFinishModalOpen(true);
					}
					index++;
					setFormLoading(true);
					setTimeout(() => {
						setFormLoading(false);
					}, 1000);
				} else {
					setError(true);
					toast.error('Por favor, complete todos los campos');
				}
			} catch (error) {
				toast.error('Hubo un error al intentar realizar esta acción.');
			} finally {
				setLoading(false);
			}
		} else {
			const isPrevious = true;
			fetchCommunication(isPrevious);
			Analytics.track(
				'campaign',
				`create-campaign-last-step-${type}`,
				campaign[type],
			);
		}

		if (!isLast) {
			setActive(index);
			if (index > completed) {
				setCompleted(index);
			}
		}
	};

	const handleStepper = (value: any) => {
		const stepSelected = value.id;

		if (stepSelected === completed) {
			setActive(completed);
		} else if (stepSelected < completed) {
			setActive(stepSelected);
		} else {
			if (stepSelected <= active) setActive(stepSelected);
		}
	};

	const handleFetchedCampaign = async (campaignComms: any) => {
		const initialCampaign = JSON.parse(JSON.stringify(campaign));

		campaignComms.forEach((campaignComm: any) => {
			const type = campaignComm.communicationType.id.split('-').pop();

			initialCampaign[type] = {
				communicationTypeId: campaignComm.communicationType.id,
				name: campaignComm.name || '',
				messageTitle: campaignComm.messageTitle || '',
				messageBody: campaignComm.useCustomTemplate
					? campaignComm.messageBody || ''
					: '',
				messageFile: campaignComm.messageFile || '',
				sendingDate: campaignComm.sendingDateTime.split('T').shift() || '',
				id: campaignComm.id,
				sendingTime: campaignComm.sendingDateTime
					? campaignComm.sendingDateTime.substring(11, 16)
					: '',
				useCustomTemplate: campaignComm.useCustomTemplate ?? false,
				fileName: campaignComm.fileName,
			};
		});

		setCampaign({ ...initialCampaign });
	};

	const handleCreatedForms = async (
		campaignItems: any,
		setPrevious: boolean | undefined,
	) => {
		const forms = campaignItems.map((campaignComm: any) => {
			return campaignComm.communicationType.id;
		});

		const activeStep = setPrevious ? active - 1 : forms.length;

		setActive(activeStep);
		setCompleted(forms.length);
		setCreatedForms([...forms]);
	};

	const fetchCommunication = async (isPrevious?: boolean | undefined) => {
		try {
			setFormLoading(true);
			const { data } = await getSectionCommunication(campaignId);
			const { campaignCommunications } = data.content;

			await handleCreatedForms(campaignCommunications, isPrevious);
			await handleFetchedCampaign(campaignCommunications);
		} catch (error) {
			toast.error('Hubo un error al buscar la información.');
		} finally {
			setFormLoading(false);
		}
	};

	useEffect(() => {
		campaign[type].communicationTypeId = campaignTypes[type];
		setCampaign({ ...campaign });

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [type]);

	useEffect(() => {
		fetchCommunication();

		return () => setCampaign(initialCampaign);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		active,
		campaign,
		datesScheduled,
		isLoading,
		isLoadingUpload,
		stepperOptions,
		completed,
		type,
		formTitle,
		isFinishModalOpen,
		navigate,
		hasError,
		campaignId,
		isFormLoading,
		showModalPreviewEmailCommunication,
		handleStepper,
		setActive,
		handleClick,
		handleChange,
		handlePreviewEmailCommunication,
		handleClosePreviewEmailCommunication,
	};
};
